import React, { useEffect, useRef, useState } from "react";
import ComparativaAnualCard from "@/components/historico/ComparativaAnualCard";
import HistoricoImportModal from "@/components/historico/HistoricoImportModal";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { Loader2, Download, RefreshCw, ChevronRight, ArrowUp, ArrowDown, Minus, X, ExternalLink } from "lucide-react";
import { MetricInfo } from "@/components/MetricInfo";

/**
 * Iter230/231 · YTD Reporting Board — Estructura fiel al Excel del comité.
 * 5 pipelines (Total · Renewals · New ALL · New C&W · New >20 M€) × 9 buckets
 * + Submission Rate + Hit Rate + Estimated Hit Rate.
 * Iter231: Toggle YoY con flechas de tendencia + click en celdas → drill-down.
 */

export const BUCKET_LABELS = {
  non_strategic:      { label: "Non-Strategic",   color: "bg-slate-50 text-slate-700" },
  no_go:              { label: "No Go",           color: "bg-slate-50 text-slate-700" },
  pending_decision:   { label: "Pending decision",color: "bg-amber-50 text-amber-700" },
  submitted:          { label: "Submitted",       color: "bg-blue-50 text-blue-700" },
  lost:               { label: "Lost / Cancelled",color: "bg-rose-50 text-rose-700" },
  pending_resolution: { label: "Pending resolution", color: "bg-indigo-50 text-indigo-700" },
  won_1st:            { label: "1st position",    color: "bg-emerald-50 text-emerald-700" },
  won_awarded:        { label: "Awarded",         color: "bg-emerald-100 text-emerald-800" },
  won_signed:         { label: "Signed",          color: "bg-emerald-200 text-emerald-900" },
};

export const PIPELINE_ROWS = [
  { key: "total",     label: "Total Renewals + New Contracts", highlight: true },
  { key: "renewals",  label: "Total Renewals" },
  { key: "new_all",   label: "Total New Contracts – All" },
  { key: "new_cw",    label: "Total New Contracts – C&W",   indent: 1 },
  { key: "new_20mm",  label: "Total New Contracts – >20 M€", indent: 1 },
];

const fmtMM = (v) => `${(Number(v) || 0).toLocaleString("es-ES", { maximumFractionDigits: 1 })}`;
// E11 · Precisión para el drill-down (contratos individuales): 2 decimales, y
// nunca mostrar "0" para un importe real >0 (ESMARGANDA 0,038 M€ salía como 0).
const fmtMM2 = (v) => {
  const n = Number(v) || 0;
  if (n > 0 && n < 0.01) return "<0,01";
  return n.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const fmtPct = (v) => (v == null ? "—" : `${v.toFixed(1)}%`);

// Iter231 · Componente de flecha de tendencia (verde/rojo/gris)
function TrendArrow({ delta, pct, direction, unit = "" }) {
  if (delta == null || Math.abs(delta) < 1e-6) {
    return (
      <span className="inline-flex items-center gap-0.5 text-[9px] text-gray-400 ml-1" title="Sin cambios">
        <Minus size={8}/>
      </span>
    );
  }
  const cls = direction === "good" ? "text-emerald-600" : direction === "bad" ? "text-rose-600" : "text-gray-400";
  const Icon = delta > 0 ? ArrowUp : ArrowDown;
  const pctStr = pct == null ? "" : ` (${pct > 0 ? "+" : ""}${pct.toFixed(0)}%)`;
  const deltaStr = `${delta > 0 ? "+" : ""}${Math.abs(delta) >= 100 ? Math.round(delta) : delta.toFixed(1)}${unit}`;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[9px] font-semibold ml-1 ${cls}`} title={`YoY ${deltaStr}${pctStr}`}>
      <Icon size={8}/>{deltaStr}
    </span>
  );
}

// Iter231 · Modal de drill-down (exportado — Entrega D, 12/10/2026, lo
// reutiliza Business Review para el mismo "ver contratos" por celda, con
// el mes añadido como filtro adicional cuando ctx.month está presente).
const DRILLDOWN_MESES = ["", "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

export function DrillDownModal({ ctx, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!ctx) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          year: String(ctx.year),
          pipeline: ctx.pipeline,
          bucket: ctx.bucket,
        });
        if (ctx.month) params.append("month", String(ctx.month));
        if (ctx.dg && ctx.dg !== "all") params.append("direccion_general", ctx.dg);
        if (ctx.minMm) params.append("min_mm", String(ctx.minMm));
        if (ctx.regla) params.append("regla", ctx.regla);
        const { data } = await axios.get(`${API}/ytd-board/drill-down?${params}`);
        if (alive) setData(data);
      } catch (e) { console.error(e); if (alive) setData({ rows: [], total: 0 }); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [ctx]);

  if (!ctx) return null;
  const pipelineLabel = PIPELINE_ROWS.find(r => r.key === ctx.pipeline)?.label || ctx.pipeline;
  const bucketLabel = BUCKET_LABELS[ctx.bucket]?.label || ctx.bucket;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="ytd-drilldown-modal">
      <div className="bg-white rounded-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-slate-800 text-white">
          <div>
            {/* P0-03 (plan cierre POC/Comité 12/10/2026) · el título decía
                siempre "Año {year}" aunque se abriera desde Business Review
                con un mes concreto seleccionado — ahora refleja el alcance
                real del filtro aplicado. */}
            <p className="text-[10px] uppercase tracking-widest opacity-70">
              Drill-down · {ctx.month ? `${DRILLDOWN_MESES[ctx.month]} ${ctx.year}` : `Año ${ctx.year}`}
            </p>
            <h3 className="text-sm font-bold font-['Outfit']">{pipelineLabel} · {bucketLabel}</h3>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white" data-testid="ytd-drilldown-close"><X size={16}/></button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-40"><Loader2 size={20} className="animate-spin text-gray-300"/></div>
          ) : data?.rows?.length === 0 ? (
            <div className="text-center text-gray-400 text-sm p-10 italic">Sin licitaciones en esta celda.</div>
          ) : (
            <table className="text-[11px] w-full">
              <thead className="bg-slate-100 text-slate-700 sticky top-0 z-10">
                <tr>
                  <th className="text-left px-3 py-2">Expediente</th>
                  <th className="text-left px-3 py-2">Cliente</th>
                  <th className="text-left px-3 py-2">Tipo</th>
                  <th className="text-left px-3 py-2">DG</th>
                  <th className="text-right px-3 py-2">Importe (M€)</th>
                  <th className="text-left px-3 py-2">Estado · Fase</th>
                  <th className="text-left px-3 py-2">Presentación</th>
                  <th className="w-10"/>
                </tr>
              </thead>
              <tbody>
                {data.rows.map(r => (
                  <tr key={r.id} className="border-b border-gray-100 hover:bg-blue-50" data-testid={`ytd-drilldown-row-${r.id}`}>
                    <td className="px-3 py-2 font-mono text-gray-700">{r.expediente || "—"}</td>
                    <td className="px-3 py-2">{r.cliente || r.titulo || "—"}</td>
                    <td className="px-3 py-2 uppercase">{r.tipo_contratacion || "—"}</td>
                    <td className="px-3 py-2 text-gray-500">{r.direccion_general || "—"}</td>
                    <td className="px-3 py-2 text-right font-semibold tabular-nums">{fmtMM2(r.value_mm)}</td>
                    <td className="px-3 py-2">
                      <span className="inline-block bg-slate-100 text-slate-800 px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase">{r.estado_licitacion || "—"}</span>
                      {r.fase_adjudicacion && <span className="text-[9px] text-gray-500 ml-1">{r.fase_adjudicacion}</span>}
                    </td>
                    <td className="px-3 py-2 text-gray-500">{(r.fecha_presentacion || "").slice(0, 10)}</td>
                    <td className="px-2 py-2 text-right">
                      <a href={`/ofertas-comercial?open=${r.id}`} target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-800" title="Abrir en Licitaciones">
                        <ExternalLink size={12}/>
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-50 font-bold sticky bottom-0">
                  <td colSpan="4" className="px-3 py-2 text-right text-gray-600">Total · {data.total} licitaciones</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#007934]">{fmtMM2(data.total_mm ?? data.rows.reduce((s, r) => s + (r.value_mm || 0), 0))}</td>
                  <td colSpan="3"/>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// Cuadre feedback · Modal "Sin fecha" — lista las ofertas que clasifican en el
// board pero no tienen fecha real (no se imputan a ningún año) para corregirlas.
export function SinFechaModal({ ctx, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!ctx) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ regla: ctx.regla, min_mm: String(ctx.minMm || 0) });
        if (ctx.dg && ctx.dg !== "all") params.append("direccion_general", ctx.dg);
        const { data } = await axios.get(`${API}/ytd-board/sin-fecha?${params}`);
        if (alive) setData(data);
      } catch (e) { console.error(e); if (alive) setData({ rows: [], total: 0 }); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [ctx]);

  if (!ctx) return null;
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="ytd-sin-fecha-modal">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-amber-600 text-white">
          <div>
            <p className="text-[10px] uppercase tracking-widest opacity-80">Ofertas sin fecha</p>
            <h3 className="text-sm font-bold font-['Outfit']">No imputadas a ningún año — revisa sus fechas</h3>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="ytd-sin-fecha-close"><X size={16}/></button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-40"><Loader2 size={20} className="animate-spin text-gray-300"/></div>
          ) : data?.rows?.length === 0 ? (
            <div className="text-center text-gray-400 text-sm p-10 italic">No hay ofertas sin fecha.</div>
          ) : (
            <table className="text-[11px] w-full">
              <thead className="bg-amber-50 text-amber-800 sticky top-0 z-10">
                <tr>
                  <th className="text-left px-3 py-2">Expediente</th>
                  <th className="text-left px-3 py-2">Cliente</th>
                  <th className="text-left px-3 py-2">DG</th>
                  <th className="text-right px-3 py-2">Importe (M€)</th>
                  <th className="text-left px-3 py-2">Estado · Fase</th>
                  <th className="w-10"/>
                </tr>
              </thead>
              <tbody>
                {data.rows.map(r => (
                  <tr key={r.id} className="border-b border-gray-100 hover:bg-amber-50/60" data-testid={`ytd-sin-fecha-row-${r.id}`}>
                    <td className="px-3 py-2 font-mono text-gray-700">{r.expediente || "—"}</td>
                    <td className="px-3 py-2">{r.cliente || r.titulo || "—"}</td>
                    <td className="px-3 py-2 text-gray-500">{r.direccion_general || "—"}</td>
                    <td className="px-3 py-2 text-right font-semibold tabular-nums">{fmtMM2(r.value_mm)}</td>
                    <td className="px-3 py-2">
                      <span className="inline-block bg-slate-100 text-slate-800 px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase">{r.estado_licitacion || "—"}</span>
                      {r.fase_adjudicacion && <span className="text-[9px] text-gray-500 ml-1">{r.fase_adjudicacion}</span>}
                    </td>
                    <td className="px-2 py-2 text-right">
                      <a href={`/ofertas-comercial?open=${r.id}`} target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-800" title="Abrir en Licitaciones">
                        <ExternalLink size={12}/>
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-amber-50 font-bold sticky bottom-0">
                  <td colSpan="3" className="px-3 py-2 text-right text-gray-600">Total · {data.total} {data.total === 1 ? "oferta" : "ofertas"}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-amber-700">{fmtMM2(data.total_mm ?? data.rows.reduce((s, r) => s + (r.value_mm || 0), 0))}</td>
                  <td colSpan="2"/>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default function YtdBoard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [dg, setDG] = useState("all");
  const [compare, setCompare] = useState(false);
  const [minMm, setMinMm] = useState(0); // Iter283 · filtro de importe persistente
  // Cuadre 22/10/2026 · P4 — conmutador "regla Excel" (comité) / "regla plataforma" (histórico)
  const [regla, setRegla] = useState("excel");
  const [drillCtx, setDrillCtx] = useState(null);
  const [showHist, setShowHist] = useState(false);   // import histórico (año anterior)
  const [histKey, setHistKey] = useState(0);         // refresca la comparativa tras importar
  const [showSinFecha, setShowSinFecha] = useState(false);  // modal "Sin fecha"
  const [prefsReady, setPrefsReady] = useState(false);       // regla guardada ya resuelta
  const loadSeq = useRef(0);                                 // descarta respuestas obsoletas

  const load = async () => {
    const seq = ++loadSeq.current;
    setLoading(true);
    try {
      const params = new URLSearchParams({ year: String(year), compare: String(compare), min_mm: String(minMm), regla });
      if (dg !== "all") params.append("direccion_general", dg);
      const { data } = await axios.get(`${API}/ytd-board?${params}`);
      if (seq === loadSeq.current) setData(data);
    } catch (e) { console.error(e); }
    finally { if (seq === loadSeq.current) setLoading(false); }
  };

  // Cuadre feedback · la regla de KPIs se persiste por usuario (default "excel").
  // Se carga ANTES del primer fetch del board para no lanzar dos cargas ni
  // arriesgar que la respuesta "excel" pise la de "plataforma" (race).
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/user-prefs/ytd-regla`);
        if (data?.regla) setRegla(data.regla);
      } catch (e) { /* mantener default 'excel' */ }
      finally { setPrefsReady(true); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (prefsReady) load(); }, [prefsReady, year, dg, compare, minMm, regla]);

  const changeRegla = (r) => {
    setRegla(r);
    axios.put(`${API}/user-prefs/ytd-regla`, { regla: r }).catch((e) => console.error(e));
  };

  const exportCsv = () => {
    if (!data) return;
    const rows = [
      ["Pipeline", ...Object.keys(BUCKET_LABELS).flatMap(b => [`${BUCKET_LABELS[b].label} (M€)`, `${BUCKET_LABELS[b].label} (#)`]),
        "Submission Rate (val)", "Submission Rate (vol)", "Hit Rate (val)", "Hit Rate (vol)", "Est. Hit Rate (val)", "Est. Hit Rate (vol)"],
    ];
    for (const r of PIPELINE_ROWS) {
      const p = data.pipelines[r.key] || {};
      const rt = data.rates[r.key] || {};
      const cells = [r.label];
      for (const b of Object.keys(BUCKET_LABELS)) {
        cells.push(fmtMM(p[b]?.value_mm || 0), p[b]?.volume ?? 0);
      }
      cells.push(fmtPct(rt.submission_rate_value), fmtPct(rt.submission_rate_volume),
                 fmtPct(rt.hit_rate_value),        fmtPct(rt.hit_rate_volume),
                 fmtPct(rt.estimated_hit_rate_value), fmtPct(rt.estimated_hit_rate_volume));
      rows.push(cells);
    }
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob([`\ufeff${csv}`], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ytd_board_${year}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 400);
  };

  if (loading || !data) {
    return <div className="flex items-center justify-center h-96"><Loader2 size={22} className="animate-spin text-gray-400"/></div>;
  }

  const openDrill = (pipeline, bucket) => setDrillCtx({ year, pipeline, bucket, dg, minMm, regla });

  return (
    <div className="space-y-4 p-4 max-w-full overflow-x-auto" data-testid="ytd-board">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-xl font-bold text-gray-900 font-['Outfit']">YTD Board — Board Reporting</h1>
          <p className="text-xs text-gray-500">
            Datos en vivo desde Licitaciones · Generado {new Date(data.generated_at).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" })} · Año {data.year}
            <span className={`ml-1.5 font-semibold ${regla === "excel" ? "text-[#007934]" : "text-slate-600"}`}>· {regla === "excel" ? "Regla Excel (comité: Servicios · NC+R · ofertado %UTE)" : "Regla plataforma (histórico)"}</span>
            {compare && data.previous_year && <span className="ml-1 text-slate-500">· Comparando con {data.previous_year}</span>}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-start sm:justify-end w-full sm:w-auto sm:shrink-0">
          {/* Cuadre 22/10/2026 · P4 — conmutador de regla de cálculo de KPIs */}
          <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden text-xs" data-testid="ytd-regla-switch">
            <button
              onClick={() => changeRegla("excel")}
              data-testid="ytd-regla-excel"
              title="Reglas del comité: sólo Servicios Urbanos, Nuevos + Renovaciones (excluye ADC), valorado con el ofertado %UTE y Non-strategic por la columna MARKET"
              className={`px-2.5 py-1 font-semibold transition-colors ${regla === "excel" ? "bg-[#007934] text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
            >
              Regla Excel
            </button>
            <button
              onClick={() => changeRegla("plataforma")}
              data-testid="ytd-regla-plataforma"
              title="Reglas históricas de la plataforma: todas las DG y tipos, Non-strategic por el flag 'estratégica'"
              className={`px-2.5 py-1 font-semibold border-l border-gray-200 transition-colors ${regla === "plataforma" ? "bg-slate-700 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
            >
              Regla plataforma
            </button>
          </div>
          <label className="flex items-center gap-1.5 text-xs text-gray-700 border rounded px-2 py-1 hover:bg-gray-50 cursor-pointer" data-testid="ytd-compare-toggle">
            <input type="checkbox" checked={compare} onChange={e => setCompare(e.target.checked)} className="accent-[#007934]"/>
            Comparar YoY
          </label>
          <select value={year} onChange={e => setYear(parseInt(e.target.value))} className="border rounded px-2 py-1 text-sm" data-testid="ytd-year-select">
            {[0, 1, 2, 3].map(o => <option key={o} value={new Date().getFullYear() - o}>{new Date().getFullYear() - o}</option>)}
          </select>
          <select value={regla === "excel" ? "SERVICIOS" : dg} onChange={e => setDG(e.target.value)} disabled={regla === "excel"} title={regla === "excel" ? "La regla Excel fija la DG en Servicios Urbanos (criterio del comité)" : undefined} className="border rounded px-2 py-1 text-sm w-[150px] disabled:bg-gray-100 disabled:text-gray-400" data-testid="ytd-dg-select">
            {/* C01/CRM01 (CRM audit 2026-10-13) · claves canónicas compartidas
                con Mercado: la BD sólo tiene SERVICIOS y TRATAMIENTO. Antes el
                selector enviaba "DG Servicios Urbanos"/"DG Residuos" y DGs
                inexistentes (Aguas/Mantenimiento/Energía) que no casaban con
                ningún dato → todo a cero al filtrar. */}
            <option value="all">Todas las DG</option>
            <option value="SERVICIOS">Servicios Urbanos</option>
            <option value="TRATAMIENTO">Residuos</option>
          </select>
          {/* Iter283 · Importe ≥ X M€ — filtro persistente pedido por el usuario */}
          <div
            data-testid="ytd-filter-importe-wrap"
            title="Filtra el board excluyendo contratos por debajo del umbral"
            className={`inline-flex items-center gap-1.5 border rounded-lg px-2 py-1 text-[11px] ${
              minMm > 0 ? "border-sky-300 bg-sky-50" : "border-gray-200 bg-white"
            }`}>
            <span className={minMm > 0 ? "font-semibold text-sky-700" : "text-gray-500"}>Importe ≥</span>
            <input
              type="range" min={0} max={30} step={1}
              value={minMm}
              onChange={(e) => setMinMm(Number(e.target.value))}
              data-testid="ytd-filter-importe"
              className="accent-sky-500 w-20"
            />
            <span className={`font-mono w-12 text-right ${minMm > 0 ? "text-sky-700 font-bold" : "text-gray-500"}`}>{minMm}M€</span>
          </div>
          <button onClick={load} data-testid="ytd-refresh" className="border rounded px-2 py-1 text-sm hover:bg-gray-50" title="Recargar"><RefreshCw size={13}/></button>
          <button onClick={exportCsv} className="bg-[#007934] text-white rounded px-3 py-1.5 text-xs font-semibold flex items-center gap-1 hover:bg-[#006528]" data-testid="ytd-export-csv">
            <Download size={12}/> Export CSV
          </button>
        </div>
      </div>

      {/* Comparativa interanual (2025 vs año en curso) desde histórico importado */}
      <ComparativaAnualCard year={year} onImport={() => setShowHist(true)} refreshKey={histKey} />
      {showHist && <HistoricoImportModal onClose={() => setShowHist(false)} onImported={() => setHistKey(k => k + 1)} />}

      {/* Cuadre feedback · aviso de ofertas sin fecha — no imputadas a ningún año */}
      {data.sin_fecha?.volume > 0 && (
        <button
          type="button"
          onClick={() => setShowSinFecha(true)}
          data-testid="ytd-sin-fecha-banner"
          className="w-full flex items-center justify-between gap-3 text-left border border-amber-300 bg-amber-50 rounded-xl px-4 py-2.5 hover:bg-amber-100 transition-colors"
        >
          <span className="text-[12px] text-amber-800">
            <b>{data.sin_fecha.volume}</b> {data.sin_fecha.volume === 1 ? "oferta sin fecha" : "ofertas sin fecha"} de firma/adjudicación/presentación
            {" "}(<b>{fmtMM(data.sin_fecha.value_mm)}</b> M€) — no se imputan a ningún año. Revisa sus fechas para que entren en el cálculo YTD.
          </span>
          <span className="text-[11px] font-semibold text-amber-700 whitespace-nowrap inline-flex items-center gap-1">
            Ver listado <ChevronRight size={12}/>
          </span>
        </button>
      )}

      {/* Tabla principal — sticky pipeline column + horizontal scroll para buckets */}
      <div className="overflow-x-auto border border-gray-200 rounded-xl bg-white">
        <table className="text-[11px] w-full">
          <thead className="bg-slate-800 text-white">
            <tr>
              <th className="sticky left-0 bg-slate-800 z-10 text-left px-3 py-2 min-w-[260px]">Pipeline</th>
              {Object.entries(BUCKET_LABELS).map(([k, v]) => (
                <th key={k} colSpan={2} className={`px-2 py-2 text-center border-l border-slate-700 uppercase text-[9px] tracking-wide ${v.color.split(" ")[0]}/40`}>
                  {v.label}
                </th>
              ))}
              <th colSpan={2} className="px-2 py-2 text-center border-l border-slate-700 uppercase text-[9px] tracking-wide bg-cyan-900">
                <span className="inline-flex items-center gap-1 justify-center">
                  Submission Rate
                  <MetricInfo testId="metric-info-submission-rate-board" nombre="Submission Rate (YTD Board)"
                    formula="Presentadas (Submitted + Pendiente resolución + 1º Clasificado + Adjudicada + Firmada + Perdida) / Analizadas (+ No Estratégica + No-Go + Pendiente decisión)"
                    poblacion="Cierre oficial importado, mismo periodo que el resto del tablero"
                    unidad="%" corte="Año/periodo seleccionado en este tablero"/>
                </span>
              </th>
              <th colSpan={2} className="px-2 py-2 text-center border-l border-slate-700 uppercase text-[9px] tracking-wide bg-emerald-900">
                <span className="inline-flex items-center gap-1 justify-center">
                  Hit Rate
                  <MetricInfo testId="metric-info-hit-rate-board" nombre="Hit Rate (YTD Board)"
                    formula="Firmadas / (Firmadas + Perdidas) — por Value (M€) y por Volume (nº)"
                    poblacion="Cierre oficial importado (Won signed + Lost), no incluye pendientes de resolución ni todo lo presentado"
                    unidad="%"
                    corte="Año/periodo seleccionado en este tablero"
                    filtros="⚠ Distinta definición de 'Hit rate' usada en Licitaciones y KPIs YTD, que calcula Adjudicadas/Presentadas sobre la cartera operativa (incluye pendientes). Ambas son correctas para su propósito, no son comparables entre sí."/>
                </span>
              </th>
              <th colSpan={2} className="px-2 py-2 text-center border-l border-slate-700 uppercase text-[9px] tracking-wide bg-emerald-800">
                <span className="inline-flex items-center gap-1 justify-center">
                  Est. Hit Rate
                  <MetricInfo testId="metric-info-est-hit-rate-board" nombre="Hit Rate Estimado (YTD Board)"
                    formula="Firmadas / (Firmadas + Perdidas + Pendientes de resolución + Adjudicada pendiente de firma + 1º Clasificado)"
                    poblacion="Igual que Hit Rate, pero el denominador incluye resultados aún en curso (no solo cierres firmados)"
                    unidad="%" corte="Año/periodo seleccionado en este tablero"
                    filtros="Al incluir pendientes, puede subir o bajar conforme se resuelvan — no es un cierre definitivo como el Hit Rate."/>
                </span>
              </th>
            </tr>
            <tr className="bg-slate-700 text-white text-[9px]">
              <th className="sticky left-0 bg-slate-700 z-10"/>
              {Object.keys(BUCKET_LABELS).map(k => (
                <React.Fragment key={k}>
                  <th className="px-2 py-1 text-right border-l border-slate-600">Value (M€)</th>
                  <th className="px-2 py-1 text-right">Volume</th>
                </React.Fragment>
              ))}
              <th className="px-2 py-1 text-right border-l border-slate-600 bg-cyan-800">Val</th>
              <th className="px-2 py-1 text-right bg-cyan-800">Vol</th>
              <th className="px-2 py-1 text-right border-l border-slate-600 bg-emerald-800">Val</th>
              <th className="px-2 py-1 text-right bg-emerald-800">Vol</th>
              <th className="px-2 py-1 text-right border-l border-slate-600 bg-emerald-700">Val</th>
              <th className="px-2 py-1 text-right bg-emerald-700">Vol</th>
            </tr>
          </thead>
          <tbody>
            {PIPELINE_ROWS.map(r => {
              const p = data.pipelines[r.key] || {};
              const rt = data.rates[r.key] || {};
              const dp = (compare && data.deltas?.pipelines?.[r.key]) || {};
              return (
                <tr key={r.key} className={`${r.highlight ? "bg-slate-100 font-bold" : "hover:bg-gray-50"} border-b border-gray-100`} data-testid={`ytd-row-${r.key}`}>
                  <td className={`sticky left-0 z-10 px-3 py-2 text-gray-800 whitespace-nowrap ${r.highlight ? "bg-slate-100" : "bg-white"} ${r.indent ? "pl-8" : ""}`}>
                    {r.indent ? <ChevronRight size={11} className="inline mr-1 text-gray-400"/> : null}
                    {r.label}
                  </td>
                  {Object.keys(BUCKET_LABELS).map(b => {
                    const dcell = dp[b] || {};
                    return (
                      <React.Fragment key={b}>
                        <td
                          className={`px-2 py-1.5 text-right border-l border-gray-100 tabular-nums cursor-pointer hover:ring-2 hover:ring-blue-400 hover:ring-inset ${BUCKET_LABELS[b].color}`}
                          onClick={() => openDrill(r.key, b)}
                          title="Click para ver detalle"
                          data-testid={`ytd-cell-${r.key}-${b}-value`}
                        >
                          {fmtMM(p[b]?.value_mm || 0)}
                          {compare && <TrendArrow delta={dcell.value_delta} pct={dcell.value_pct} direction={dcell.direction_value} unit=""/>}
                        </td>
                        <td
                          className={`px-2 py-1.5 text-right tabular-nums cursor-pointer hover:ring-2 hover:ring-blue-400 hover:ring-inset ${BUCKET_LABELS[b].color}`}
                          onClick={() => openDrill(r.key, b)}
                          title="Click para ver detalle"
                          data-testid={`ytd-cell-${r.key}-${b}-volume`}
                        >
                          {p[b]?.volume ?? 0}
                          {compare && <TrendArrow delta={dcell.volume_delta} pct={dcell.volume_pct} direction={dcell.direction_volume} unit=""/>}
                        </td>
                      </React.Fragment>
                    );
                  })}
                  <td className="px-2 py-1.5 text-right border-l border-gray-100 tabular-nums bg-cyan-50 text-cyan-800 font-semibold">{fmtPct(rt.submission_rate_value)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums bg-cyan-50 text-cyan-800 font-semibold">{fmtPct(rt.submission_rate_volume)}</td>
                  <td className="px-2 py-1.5 text-right border-l border-gray-100 tabular-nums bg-emerald-50 text-emerald-800 font-semibold">{fmtPct(rt.hit_rate_value)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums bg-emerald-50 text-emerald-800 font-semibold">{fmtPct(rt.hit_rate_volume)}</td>
                  <td className="px-2 py-1.5 text-right border-l border-gray-100 tabular-nums bg-emerald-50 text-emerald-800">{fmtPct(rt.estimated_hit_rate_value)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums bg-emerald-50 text-emerald-800">{fmtPct(rt.estimated_hit_rate_volume)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="text-[10px] text-gray-400">
        <b>Submission Rate</b> = Submitted / Analysed &nbsp;·&nbsp;
        <b>Hit Rate</b> = Signed / (Signed + Lost) &nbsp;·&nbsp;
        <b>Est. Hit Rate</b> = (Signed + Awarded + 1st) / (Resolved + Pending Resolution)
        {compare && <span className="ml-2">· <b>Flechas YoY</b>: verde = mejora · rojo = empeora · Non-Strategic/No Go/Lost invertidos.</span>}
        &nbsp;·&nbsp;<b>Click en cualquier celda</b> para ver el detalle de licitaciones.
      </p>

      <DrillDownModal ctx={drillCtx} onClose={() => setDrillCtx(null)}/>
      <SinFechaModal ctx={showSinFecha ? { regla, dg, minMm } : null} onClose={() => setShowSinFecha(false)}/>
    </div>
  );
}
